import React from 'react';
import { US_STATES_GEO } from './us-states-outline';
import { US_COUNTIES_GEO } from './us-counties-outline';
import { WORLD_LAND_GEO } from './world-outline';

// Postal-code bubble map (UX note §58) + market-halo map (§58b share the
// projection). Self-contained SVG — no Maps API key (GeoChart markers
// mode would need one; regions mode can't do metro zoom). Equirectangular
// projection over the rows' bounding box with ~20% padding and a
// cos(mid-lat) x-correction so metro markets fill the frame instead of
// drowning in the lower 48. Radius ∝ sqrt(metric) so small zips stay
// visible; accent ramp by value. Rows without lat/lng are dropped by the
// caller (kept in its table with the footnote).
const ACCENT = '#ff6358';
export default function PostalBubbleMap({
  rows,
  // [{ lat, lng, label, sub, value }]
  hovered,
  // label currently hovered (table ↔ map link)
  onHover,
  // (label|null) => void
  onClick,
  // optional (label) => void
  height = 320,
  showLabels = false,
  // §58b market halos label every marker (rows may set quiet:true to opt out; ring:true draws a hollow dashed marker)
  counties = false,
  // draw a US county basemap (metro/postal view) instead of states
  fill = false // size the frame to the box the map sits in (grid cell) — no crop, no band
}) {
  // `fill`: measure the box and derive the frame aspect from it, so the
  // viewBox matches the cell exactly (nothing cropped, nothing letterboxed).
  const boxRef = React.useRef(null);
  const [box, setBox] = React.useState(null);
  React.useEffect(() => {
    if (!fill || !boxRef.current) return undefined;
    const el = boxRef.current;
    const upd = () => setBox({
      w: el.clientWidth,
      h: el.clientHeight
    });
    upd();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fill]);
  const pts = (rows || []).filter(r => Number.isFinite(Number(r.lat)) && Number.isFinite(Number(r.lng))).map(r => ({
    ...r,
    lat: Number(r.lat),
    lng: Number(r.lng),
    value: Number(r.value) || 0
  }));
  if (pts.length === 0) return null;
  const W = 640;
  if (fill && box && box.w > 0 && box.h > 0) height = Math.max(120, Math.round(W * box.h / box.w));
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  pts.forEach(p => {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  });

  // pad the box ~20% (never zero-size — single-point maps get a fixed pad)
  const latSpan = Math.max(maxLat - minLat, 0.2);
  const lngSpan = Math.max(maxLng - minLng, 0.2);
  minLat -= latSpan * 0.2;
  maxLat += latSpan * 0.2;
  minLng -= lngSpan * 0.2;
  maxLng += lngSpan * 0.2;
  const midLat = (minLat + maxLat) / 2;
  const xScale = Math.cos(midLat * Math.PI / 180);
  const geoW = (maxLng - minLng) * xScale;
  let geoH = maxLat - minLat;
  // Fill the frame: a flat spread of markets (e.g. Phoenix → South Florida)
  // would only use a thin band of the box. Grow the latitude range (centered)
  // until the box has the frame's aspect, so the map shows the surrounding
  // geography instead of letterboxing.
  const targetH = geoW * height / W;
  // basemap choice uses the MARKETS' box, not the grown frame (the grown box
  // can reach past the US bounds and would flip to the coarse world outline)
  const marketBox = {
    minLat,
    maxLat,
    minLng,
    maxLng
  };
  let geoWAdj = geoW;
  if (geoH < targetH) {
    const grow = (targetH - geoH) / 2;
    minLat -= grow;
    maxLat += grow;
    geoH = targetH;
  } else if (geoH > targetH) {
    // tall spread (Sarasota → Boca Raton): grow longitude instead so the
    // frame keeps the requested aspect and fills its box
    const targetW = geoH * W / height;
    const growDeg = (targetW - geoW) / 2 / Math.max(xScale, 0.01);
    minLng -= growDeg;
    maxLng += growDeg;
    geoWAdj = targetW;
  }
  const geoWFinal = geoWAdj;
  const H = Math.max(180, Math.min(height, W * geoH / Math.max(geoWFinal, 0.001)));
  // ONE scale for both axes (px per degree-equivalent), letterboxed and
  // centered: clamping H alone used to squash tall boxes (Chicago →
  // South Florida) and stretch flat ones, distorting the basemap.
  const k = Math.min(W / Math.max(geoWFinal, 0.001), H / Math.max(geoH, 0.001));
  const xOff = (W - geoWFinal * k) / 2;
  const yOff = (H - geoH * k) / 2;
  const x = lng => xOff + (lng - minLng) * xScale * k;
  const y = lat => H - yOff - (lat - minLat) * k;
  const maxV = Math.max(1, ...pts.map(p => p.value));
  const rOf = v => 4 + 22 * Math.sqrt(v / maxV);

  // Base map underlay: project each land ring through the same lat/lng math
  // and draw the ones that fall inside the view (cheap bbox cull). Keyless.
  // Basemap by context: the metro/postal view (`counties`) draws US COUNTY
  // lines so tight metro zooms have local geography behind the zips; otherwise
  // US-scale views get states and broader views (markets spanning countries)
  // get the simplified world land outline (§58b world basemap).
  const withinUs = marketBox.minLng >= -170 && marketBox.maxLng <= -60 && marketBox.minLat >= 15 && marketBox.maxLat <= 72;
  const GEO = counties ? US_COUNTIES_GEO : withinUs ? US_STATES_GEO : WORLD_LAND_GEO;
  const stroke = counties ? '#cfd4da' : '#d6d9dd';
  const strokeW = counties ? 0.6 : 0.7;
  const basemapPaths = [];
  for (let s = 0; s < GEO.length; s++) {
    const rings = GEO[s].rings || [GEO[s].r];
    for (let ri = 0; ri < rings.length; ri++) {
      const ring = rings[ri];
      let aLng = Infinity;
      let bLng = -Infinity;
      let cLat = Infinity;
      let dLat = -Infinity;
      for (let i = 0; i < ring.length; i++) {
        const lng = ring[i][0];
        const lat = ring[i][1];
        if (lng < aLng) aLng = lng;
        if (lng > bLng) bLng = lng;
        if (lat < cLat) cLat = lat;
        if (lat > dLat) dLat = lat;
      }
      if (aLng > maxLng || bLng < minLng || cLat > maxLat || dLat < minLat) continue; // off-screen
      let d = '';
      for (let i = 0; i < ring.length; i++) {
        d += (i ? 'L' : 'M') + x(ring[i][0]).toFixed(1) + ',' + y(ring[i][1]).toFixed(1) + ' ';
      }
      basemapPaths.push(d + 'Z');
    }
  }

  // Label placement: biggest first, and skip a label whose box would overlap one
  // already placed (a dense cluster such as several cities around one metro
  // otherwise stacks every name on top of each other). Hover still shows any label.
  const labelY = p => y(p.lat) - rOf(p.value) - 4 < 14 ? y(p.lat) + rOf(p.value) + 13 : y(p.lat) - rOf(p.value) - 4;
  const placed = [];
  const showLabel = new Set();
  if (showLabels) {
    pts.filter(p => !p.quiet).slice().sort((a, b) => b.value - a.value).forEach(p => {
      const w = String(p.label || '').length * 6.2 + 4;
      const cx = x(p.lng);
      const ly = labelY(p);
      const box = {
        l: cx - w / 2,
        r: cx + w / 2,
        t: ly - 11,
        b: ly + 2
      };
      if (placed.some(q => box.l < q.r && box.r > q.l && box.t < q.b && box.b > q.t)) return;
      placed.push(box);
      showLabel.add(p);
    });
  }
  return /*#__PURE__*/React.createElement("div", {
    ref: boxRef,
    style: {
      overflow: 'hidden',
      borderRadius: 8,
      background: 'var(--plk-surface-alt, #f6f6f6)',
      height: fill ? '100%' : undefined,
      minHeight: 0
    }
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: `0 0 ${W} ${H}`,
    style: {
      width: '100%',
      height: fill ? '100%' : undefined,
      display: 'block'
    },
    role: "img",
    "aria-label": "Market map"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "0",
    y: "0",
    width: W,
    height: H,
    fill: "#e9eff4"
  }), /*#__PURE__*/React.createElement("g", {
    className: "mi-basemap",
    style: {
      pointerEvents: 'none'
    }
  }, basemapPaths.map((d, i) => /*#__PURE__*/React.createElement("path", {
    key: i,
    d: d,
    fill: "#f7f5f1",
    stroke: stroke,
    strokeWidth: strokeW,
    strokeLinejoin: "round"
  }))), pts.map(p => {
    const hot = hovered === p.label;
    return /*#__PURE__*/React.createElement("g", {
      key: p.label,
      onMouseEnter: onHover ? () => onHover(p.label) : undefined,
      onMouseLeave: onHover ? () => onHover(null) : undefined,
      onClick: onClick ? () => onClick(p.label) : undefined,
      style: {
        cursor: onClick ? 'pointer' : 'default'
      }
    }, /*#__PURE__*/React.createElement("circle", {
      cx: x(p.lng),
      cy: y(p.lat),
      r: rOf(p.value),
      fill: p.ring ? 'none' : ACCENT,
      fillOpacity: hot ? 0.85 : 0.25 + 0.45 * (p.value / maxV),
      stroke: ACCENT,
      strokeWidth: hot ? 2.5 : p.ring ? 1.5 : 1,
      strokeOpacity: 0.9,
      strokeDasharray: p.ring ? '4 3' : undefined
    }, /*#__PURE__*/React.createElement("title", null, `${p.label}${p.sub ? ' · ' + p.sub : ''} — ${p.value.toLocaleString()}`)), (showLabel.has(p) || hot) && /*#__PURE__*/React.createElement("text", {
      x: x(p.lng),
      y: labelY(p),
      textAnchor: "middle",
      style: {
        fontSize: 11,
        fill: 'var(--plk-text, #333)',
        fontWeight: 600,
        pointerEvents: 'none'
      }
    }, p.label));
  })));
}

// Metro test (§58 default logic): single region OR bbox span < ~500 km.
export function isMetroScale(rows) {
  const pts = (rows || []).filter(r => Number.isFinite(Number(r.lat)) && Number.isFinite(Number(r.lng)));
  if (pts.length === 0) return false;
  const regions = new Set(pts.map(r => String(r.regionCode || '').toUpperCase()).filter(Boolean));
  if (regions.size === 1) return true;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  pts.forEach(p => {
    minLat = Math.min(minLat, Number(p.lat));
    maxLat = Math.max(maxLat, Number(p.lat));
    minLng = Math.min(minLng, Number(p.lng));
    maxLng = Math.max(maxLng, Number(p.lng));
  });
  const midLat = (minLat + maxLat) / 2 * Math.PI / 180;
  const kmLat = (maxLat - minLat) * 111;
  const kmLng = (maxLng - minLng) * 111 * Math.cos(midLat);
  return Math.max(kmLat, kmLng) < 500;
}